/**
 * Spam-protection helpers.
 *
 * E-mail addresses are never stored as plain text in the source or in the
 * rendered HTML. They are kept as reversed base64 tokens and only decoded
 * client-side when the visitor actually clicks a contact element.
 */

export type MailKey =
  | "info"
  | "jobs"
  | "infoSwiss"
  | "marketing"
  | "walter.althaus"
  | "marco.schneider"
  | "manfred.boegli"
  | "michael.kuenzi"
  | "thilo.biss"
  | "reto.fritsche"
  | "stefan.brudermann"
  | "kathrin.walter"
  | "roland.rebecchi"
  | "michelle.schaer"
  | "yannick.althaus";

const TOKENS: Record<MailKey, string> = {
  info: "oNmLnFWLzVXYoRHbhB0bm5Wa",
  jobs: "oNmLnFWLzVXYoRHbhB0ci9ma",
  infoSwiss: "zNXa3NnLzVXYoRHbhB0bm5Wa",
  marketing: "=g2YucWYtMXdhhGdsFGQn5Wa0V2ayFWb",
  "walter.althaus": "==Aaj5yZh1yc1FGa0xWYANXdhhGdsFmLyVGdsF2d",
  "marco.schneider": "=g2YucWYtMXdhhGdsFGQyVGZpVmboN2cu82YyFWb",
  "manfred.boegli": "==Aaj5yZh1yc1FGa0xWYAlGbnV2bi5CZlJnZuFWb",
  "michael.kuenzi": "==Aaj5yZh1yc1FGa0xWYAlmeuVWdr5CblFGajlWb",
  "thilo.biss": "oNmLnFWLzVXYoRHbhB0czlmYu8GbphGd",
  "reto.fritsche": "oNmLnFWLzVXYoRHbhBUZoN2c0lmcm5yb0Vmc",
  "stefan.brudermann": "==Aaj5yZh1yc1FGa0xWYA5mbh1mclRWdyJmLuFmZlR3c",
  "kathrin.walter": "==Aaj5yZh1yc1FGa0xWYAJXZ0xWY35ibpJHa0F2a",
  "roland.rebecchi": "=g2YucWYtMXdhhGdsFGQph2YjVmYlJnLk5WYs9mc",
  "michelle.schaer": "=g2YucWYtMXdhhGdsFGQyVWYoN2cuUGbsVGajlWb",
  "yannick.althaus": "=g2YucWYtMXdhhGdsFGQzVXYoRHbh5yajlmbuFWe",
};

/** Decodes a mail key into the real address (client-side only). */
export function decodeMail(key: MailKey): string {
  const token = TOKENS[key];
  if (!token) return "";
  try {
    return atob(token.split("").reverse().join(""));
  } catch {
    return "";
  }
}

/** Opens the visitor's default mail client with the decoded address. */
export function openMail(key: MailKey, subject?: string, body?: string): void {
  const address = decodeMail(key);
  if (!address) return;
  const params: string[] = [];
  if (subject) params.push(`subject=${encodeURIComponent(subject)}`);
  if (body) params.push(`body=${encodeURIComponent(body)}`);
  window.location.href = `mailto:${address}${params.length ? `?${params.join("&")}` : ""}`;
}

type Locale = string;

/** Localized default label for the contact element. */
export function mailLabel(locale: Locale = "de"): string {
  switch (locale) {
    case "en":
      return "Write an e-mail";
    case "fr":
      return "Écrire un e-mail";
    case "it":
      return "Scrivere un'e-mail";
    default:
      return "E-Mail schreiben";
  }
}
